// Light or dark: shadcn's .dark class on the document (styles/globals.css has both palettes).
// The system preference until the viewer picks one, then their pick, kept in this browser
// only — the one thing the SPA stores (guide, Enforcement: storage stays in lib/).
import { useSyncExternalStore } from "react";

export type Theme = "light" | "dark";

const KEY = "kd_theme";

function stored(): Theme | null {
  try {
    const value = localStorage.getItem(KEY);
    return value === "light" || value === "dark" ? value : null;
  } catch {
    return null; // storage refused (private mode): the system preference stands
  }
}

const systemPrefersDark = () => window.matchMedia("(prefers-color-scheme: dark)").matches;

export function preferredTheme(prefersDark: () => boolean = systemPrefersDark): Theme {
  return stored() ?? (prefersDark() ? "dark" : "light");
}

export function applyTheme(theme: Theme) {
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export function saveTheme(theme: Theme) {
  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // not kept; it still applies for this visit
  }
  applyTheme(theme);
}

export const currentTheme = (): Theme => (document.documentElement.classList.contains("dark") ? "dark" : "light");

// Whoever changes the class (the theme toggle, another tab's choice applied on load), a
// component reading the theme — the toaster — re-renders with it.
function subscribe(onChange: () => void): () => void {
  const observer = new MutationObserver(onChange);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
  return () => observer.disconnect();
}

/** The document's theme, kept current as it changes. */
export const useCurrentTheme = (): Theme => useSyncExternalStore(subscribe, currentTheme);
